import React, { useContext } from 'react';

import { type PreAlertDetails } from '@models/next/PreAlert';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { toJST } from '@utils/types';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

const propsContext = React.createContext<ExportPreAlertProps>({
  fcLogo: '',
  preAlert: undefined,
  updatedAt: '',
  logLink: '#',
});

const strings = createStrings({
  ja: {
    customerNo: 'お客様の管理番号',
    print: '印刷',
    history: '変更履歴',
    reason: '変更理由',
  },
});

const useStyles = makeMuiStyles((theme, common) => {
  return {
    root: {
      width: 595,
      padding: '57px 38px 45px 38px',
      margin: '0 auto',
      ...common.normalText,
    },
    fcHeader: {
      ...common.flexInline,
      ...common.justifyBetween,
      alignItems: 'flex-start',
    },
    fcCompanyInformationRoot: {
      ...common.flexColumn,
      alignItems: 'flex-end',
    },
    fcCompanyInformationTitle: {
      fontWeight: 'bold',
      fontSize: 14,
    },
    fcCompanyInformationData: {
      whiteSpace: 'pre',
      textAlign: 'right',
      fontSize: 12,
    },
    container: {
      margin: '10px 0',
    },
    header: {
      ...common.flexInline,
      ...common.justifyCenter,
    },
    footer: {
      ...common.flexInline,
      ...common.justifyBetween,
      fontSize: 12,
      marginTop: 9,
    },
    title: {
      fontSize: 16,
      fontWeight: 'bold',
    },
    dataContainer: {
      padding: '0 20px',
      borderTop: '3px double #000000',
      borderBottom: '3px double #000000',
    },
    definitionList: {
      ...common.flexInline,
      flexWrap: 'wrap',
      alignContent: 'space-between',
      fontSize: 12,
    },
    definitionTerm: {
      flex: '0 1 150px',
      fontWeight: 'bolder',
      marginTop: '5px',
    },
    definitionDescription: {
      ...common.flexColumn,
      '& > * + *': {
        marginTop: '0.5rem',
      },
      marginLeft: '0.5rem',
      marginRight: '0.5rem',
      flex: '0 1 calc(100% - 200px)',
      marginTop: '5px',
      minWidth: 0,
    },
    definitionDescriptionLine: {
      ...common.flexInline,
      alignItems: 'center',
      flexShrink: 1,
      minWidth: 0,
      flexWrap: 'wrap',
      rowGap: '1rem',
    },
    definitionDescriptionContext: {
      flexShrink: 1,
      minWidth: 0,
      wordBreak: 'break-word',
      marginRight: '0.5rem',
      marginLeft: '0.5rem',
    },
    reason: {
      wordBreak: 'break-all',
      whiteSpace: 'pre-wrap',
      marginRight: '0.5rem',
      marginLeft: '0.5rem',
    },
    definitionNoTerm: {
      flex: '0 1 43px',
      fontWeight: 'bolder',
    },
    definitionNoDescription: {
      margin: 0,
      flex: '0 1 calc(100% - 43px)',
    },
    lastUpdateText: {
      color: theme.palette.grey[800],
    },
    links: {
      '& > * + *': {
        marginLeft: 24,
      },
    },
    link: {
      color: theme.palette.primary.main,
      cursor: 'pointer',
    },
    updatedCurrentValue: {
      color: theme.palette.primary.main,
    },
    arrow: {
      display: 'inline-flex',
      alignItems: 'center',
      width: 20,
      flexShrink: 0,
      margin: '0rem 0.5rem',
      '&::before': {
        content: '""',
        width: '100%',
        borderBottom: '1px solid #000',
        background: '#000',
      },
      '&::after': {
        content: '""',
        width: 5,
        height: 5,
        marginLeft: -4,
        borderRight: '1px solid #000',
        borderTop: '1px solid #000',
        transform: 'rotate(45deg)',
      },
    },
  };
});

const FCLogo: React.FC = () => {
  const { fcLogo } = useContext(propsContext);

  return (
    <div>
      <img src={fcLogo} alt="FCSL" />
    </div>
  );
};

const FCInformation: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.fcCompanyInformationRoot}>
      <div className={classes.fcCompanyInformationTitle}>FC Standard Logistics Co., Ltd.</div>
      <div className={classes.fcCompanyInformationData}>
        14th Floor, Kurabo Annex Building
        <br />
        4-11, 2-chome Kyutaromachi, Chuo-ku
        <br />
        Osaka 541-0056, Japan
        <br />
        Tel:(81)06-6266-8588
        <br />
        Fax:(81)06-6266-8566
      </div>
    </div>
  );
};

const DataContainer: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;
  const { updatedAt, logLink } = useContext(propsContext);
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <section>
      <header className={classes.header}>
        <div className={classes.title}>
          <span>Pre-Alert</span>
        </div>
      </header>
      <div className={classes.dataContainer}>{children}</div>
      <footer className={classes.footer}>
        <div className={classes.lastUpdateText}>
          <span>
            Last update: <time>{updatedAt == null ? '-' : toJST(new Date(updatedAt))}</time>
          </span>
        </div>
        <div className={classes.links}>
          <a className={classes.link} onClick={window.print}>
            {strings.print}
          </a>
          <Link className={classes.link} to={logLink}>
            {strings.history}
          </Link>
        </div>
      </footer>
    </section>
  );
};

const PreAlertItem: React.FC<{
  term: string;
  unit?: string;
  value?: string | number | null;
}> = (props) => {
  const { term, unit = '', value } = props;
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const formattedValue = value ? `${value} ${unit}` : '';

  return (
    <>
      <dt className={classes.definitionTerm}>{term}</dt>
      <dd className={classes.definitionDescription}>
        <span className={classes.definitionDescriptionContext}>{formattedValue}</span>
      </dd>
    </>
  );
};

const PreAlertNoItem: React.FC<{
  no?: string;
}> = (props) => {
  const { no } = props;
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <>
      <dt className={classes.definitionNoTerm}>NO</dt>
      <dd className={classes.definitionNoDescription}>{no ?? '-'}</dd>
    </>
  );
};

const PreAlertItemList: React.FC = () => {
  const { preAlert } = useContext(propsContext);
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <dl className={classes.definitionList}>
      <PreAlertNoItem no={preAlert?.no} />
      <PreAlertItem term={strings.customerNo} value={preAlert?.customer_no} />
      <PreAlertItem term="Refer Number" value={preAlert?.refer_number} />
      <PreAlertItem term="Shipper" value={preAlert?.shipper} />
      <PreAlertItem term="Consignee" value={preAlert?.consignee} />
      <PreAlertItem term="HBL" value={preAlert?.house_bl_number} />
      <PreAlertItem term="Carrier" value={preAlert?.carrier} />
      <PreAlertItem term="Vessel" value={preAlert?.vessel} />
      <PreAlertItem term="Voyage" value={preAlert?.voyage} />
      <PreAlertItem term="POL" value={preAlert?.pol} />
      <PreAlertItem term="POD" value={preAlert?.pod} />
      <PreAlertItem
        term="ETD"
        value={preAlert?.etd ? dayjs(preAlert.etd).format('YYYY/MM/DD') : ''}
      />
      <PreAlertItem
        term="ETA"
        value={preAlert?.eta ? dayjs(preAlert.eta).format('YYYY/MM/DD') : ''}
      />
      <PreAlertItem term="Quantity" value={preAlert?.quantity} />
      <PreAlertItem term="Packages" value={preAlert?.packages} />
      <PreAlertItem term="Shipping Mark " value={preAlert?.shipping_mark} />
      <PreAlertItem term="Commodity" value={preAlert?.commodity} />
      <PreAlertItem term="Gross Weight" unit="kg" value={preAlert?.gross_weight} />
      <PreAlertItem term="Measurement" unit="CBM" value={preAlert?.measurement} />
    </dl>
  );
};

export const ExportPreAlert: React.FC<ExportPreAlertProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <propsContext.Provider value={props}>
      <main className={classes.root}>
        <section>
          <header className={classes.fcHeader}>
            <div>
              <FCLogo />
            </div>
            <div>
              <FCInformation />
            </div>
          </header>
          <div className={classes.container}>
            <DataContainer>
              <PreAlertItemList />
            </DataContainer>
          </div>
        </section>
      </main>
    </propsContext.Provider>
  );
};

export type PreAlertDefinitions = {
  [Key in keyof PreAlertDetails]: {
    initialValue: NonNullable<PreAlertDetails[Key]>;
    currentValue: NonNullable<PreAlertDetails[Key]>;
    reason?: string;
  };
};

export interface ExportPreAlertProps {
  fcLogo: string;
  updatedAt?: string;
  preAlert?: PreAlertDetails;
  logLink: string;
}
